import blankAnamnesisImg from '@/assets/images/blank-anamnesis.webp';
import fileUploadImg from '@/assets/images/file-upload.webp';
import withTemplateImg from '@/assets/images/with-template.webp';
import { Button } from '@/components/ui/button';
import AnamnesisUploadStep from './AnamnesisUploadStep';
import { resolveTemplateForPatient } from './resolve-template';
import TemplateGallery from './TemplateGallery';
import TemplateOptionCard from './TemplateOptionCard';
import type { AnamnesisTemplateId } from './templates';
import type { Patient } from '@/types/patient';

type Props = {
    /** Required even when the full record is missing: the upload posts to it. */
    patientId: string;
    patient?: Patient;
    /** Controlled by the owner, which also renders the single back control. */
    showGallery: boolean;
    onShowGallery: () => void;
    /** Also owner-controlled, for the same reason. Wins over the gallery. */
    showUpload: boolean;
    onShowUpload: () => void;
    onSelect: (templateId: AnamnesisTemplateId) => void;
    /** The file is already saved when this fires — just leave and open it. */
    onUploaded: () => void;
};

/**
 * How to start an anamnesis: blank, from the template that matches the
 * patient's age, or by uploading a ready file. The age is resolved from the
 * patient record instead of asked about — the full gallery stays one click away
 * for the cases the age misses (and is the writing default when no birth date
 * is registered).
 *
 * Purely presentational: the gallery/upload toggles live in the owner, so the
 * modal header can carry one back button covering every view.
 */
export default function AnamnesisStartStep({
    patientId,
    patient,
    showGallery,
    onShowGallery,
    showUpload,
    onShowUpload,
    onSelect,
    onUploaded,
}: Props) {
    const resolved = resolveTemplateForPatient(patient);

    if (showUpload) {
        return <AnamnesisUploadStep patientId={patientId} onUploaded={onUploaded} />;
    }

    if (showGallery || resolved.kind !== 'matched') {
        return (
            <div className="space-y-4">
                {resolved.kind === 'unknown-age' && (
                    <p className="text-xs text-muted-foreground">
                        Sem data de nascimento cadastrada — escolha o modelo para este paciente.
                    </p>
                )}
                <TemplateGallery onSelect={onSelect} />
                <p className="text-right text-xs text-muted-foreground">
                    Já tem a anamnese pronta?{' '}
                    <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={onShowUpload}
                    >
                        Enviar arquivo
                    </Button>
                </p>
            </div>
        );
    }

    const { template, templateId, age } = resolved;

    return (
        <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
                <TemplateOptionCard
                    name="Sem modelo"
                    meta={['Página em branco']}
                    image={blankAnamnesisImg}
                    onSelect={() => onSelect('blank')}
                />
                <TemplateOptionCard
                    name="Com modelo"
                    // The patient's own age, not the template's range: it is what
                    // justifies this particular suggestion.
                    meta={[template.name, `${age} ${age === 1 ? 'ano' : 'anos'}`]}
                    sections={template.sections.map((section) => section.title)}
                    image={withTemplateImg}
                    onSelect={() => onSelect(templateId)}
                />
                <TemplateOptionCard
                    name="Enviar arquivo"
                    meta={['.docx, .txt ou .pdf']}
                    image={fileUploadImg}
                    onSelect={onShowUpload}
                />
            </div>
            <p className="text-right text-xs text-muted-foreground">
                O modelo vem da idade do paciente.{' '}
                <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs"
                    onClick={onShowGallery}
                >
                    Usar outro modelo
                </Button>
            </p>
        </div>
    );
}
